import { useEffect, useRef, type ReactNode } from 'react';
import { ValueCell } from '~/components/Fields/LabeledValue';
import PropertyEditButton from '~/components/Fields/PropertyEditButton';
import InlineEdit from '~/components/InlineEdit';

/**
 * A value of a properties list that edits in place: the value, then a
 * pencil while it is `editable`; the pencil opens the editor, seeded with
 * `seed`, in the value's place. Enter or a blur submits, Escape discards.
 * A `multiline` value (DICOM VR ST) keeps its line breaks: Enter adds one,
 * Ctrl/Cmd+Enter or Save submits, and clicking away leaves the editor open.
 *
 * When the editor closes, focus returns to the pencil, so a keyboard user
 * keeps their place in the list. When the value stops being editable while
 * its editor is open, the editor closes without a submit and focus goes to
 * the value.
 */
export default function EditablePropertyValue({
    display,
    editable,
    editing,
    onEditingChange,
    seed,
    onSubmit,
    validate,
    editLabel,
    fieldLabel,
    selectAllOnOpen = false,
    multiline = false,
    fontWeight,
    buttonTestId,
}: {
    /** The value as the list shows it. */
    display: ReactNode;
    /** Whether the value can be edited now; the pencil shows only then. */
    editable: boolean;
    /** Whether the editor is open. */
    editing: boolean;
    onEditingChange: (editing: boolean) => void;
    /** The text the editor opens with. */
    seed: string;
    onSubmit: (text: string) => void;
    /** The reason a text cannot be used, or null. */
    validate?: (text: string) => string | null;
    /** The pencil's accessible name. */
    editLabel: string;
    /** The editor's accessible name. */
    fieldLabel: string;
    selectAllOnOpen?: boolean;
    /** Keep the line breaks in the value. See {@link InlineEdit}. */
    multiline?: boolean;
    fontWeight: number;
    buttonTestId?: string;
}) {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const valueRef = useRef<HTMLDivElement>(null);
    const wasEditingRef = useRef(false);

    useEffect(() => {
        if (editing && !editable) {
            onEditingChange(false);
        }
    }, [editing, editable, onEditingChange]);

    // The input that had focus unmounts as the editor closes, which would
    // leave keyboard and screen reader users at document.body. On a close
    // because the value locked there is no pencil, so the value takes focus.
    useEffect(() => {
        if (wasEditingRef.current && !editing) {
            (triggerRef.current ?? valueRef.current)?.focus();
        }
        wasEditingRef.current = editing;
    }, [editing]);

    if (editing && editable) {
        return (
            <InlineEdit
                value={seed}
                onSubmit={onSubmit}
                onCancel={() => onEditingChange(false)}
                validate={validate}
                ariaLabel={fieldLabel}
                selectAllOnOpen={selectAllOnOpen}
                multiline={multiline}
                fontSize={12}
                fontWeight={fontWeight}
            />
        );
    }
    return (
        <ValueCell ref={valueRef} tabIndex={-1}>
            {display}
            {editable && (
                <PropertyEditButton
                    ref={triggerRef}
                    aria-label={editLabel}
                    onClick={() => onEditingChange(true)}
                    data-testid={buttonTestId}
                />
            )}
        </ValueCell>
    );
}
